import React, { useEffect, useCallback, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import CheckTransactionsTable from '../checks/CheckTransactionsTable';
import { BalanceSummaryCards } from '../checks/CheckBalanceCards';
import ExchangeRateMissingAlert from '../checks/ExchangeRateMissingAlert';
import { useAsyncAction } from '../../hooks/useAsyncAction';
import { buildClientBalanceCardEntries } from '../../utils/checkBalanceDisplay';
import { getApiErrorInfo } from '../../utils/apiErrors';
import { useFeedback, usePageNotice } from '../../context/FeedbackContext';

function ClientBalanceTab({
  clientId,
  active,
  isMobile,
  isManager,
}) {
  const { t } = useTranslation();
  const { confirm, showError } = useFeedback();
  const { run: runAction, isPending } = useAsyncAction();
  const [clientBalance, setClientBalance] = useState(null);
  const [clientTransactions, setClientTransactions] = useState([]);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceError, setBalanceError] = useState(null);

  const fetchClientBalance = useCallback(async (cId) => {
    setBalanceLoading(true);
    setBalanceError(null);
    try {
      const res = await axios.get(`/api/payments/${cId}/balance`);
      setClientBalance(res.data.balance);
      setClientTransactions(res.data.transactions || []);
    } catch (error) {
      console.error('Failed to fetch balance:', error);
      setBalanceError(getApiErrorInfo(error));
      setClientBalance(null);
      setClientTransactions([]);
    } finally {
      setBalanceLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!active || !clientId) return;
    fetchClientBalance(clientId);
  }, [active, clientId, fetchClientBalance]);

  const handleCancelDeposit = async (paymentId) => {
    if (!(await confirm(t('cashiering.confirmCancelDeposit')))) return;
    await runAction(async () => {
      try {
        await axios.delete(`/api/payments/${paymentId}`);
        if (clientId) fetchClientBalance(clientId);
      } catch (error) {
        showError(error.response?.data?.error || t('cashiering.failedToCancelDeposit'));
      }
    }, `cancel-payment-${paymentId}`);
  };

  const balanceHint = `${t('clients.balanceTotalItemsHint')} ${t('clients.balanceAmountDueHint')}`;

  usePageNotice(
    `client-balance-error-${clientId}`,
    active && !clientBalance && balanceError && balanceError.code !== 'MISSING_EXCHANGE_RATE'
      ? { message: balanceError.message, severity: 'error', persist: true }
      : null,
  );
  usePageNotice(
    `client-balance-hint-${clientId}`,
    active && clientBalance
      ? { message: balanceHint, severity: 'info', persist: true }
      : null,
  );

  if (!active) return null;

  if (balanceLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}><CircularProgress size={32} /></Box>
    );
  }

  const currency = clientBalance?.checkCurrency;
  const balanceCards = buildClientBalanceCardEntries({
    totalItems: clientBalance?.totalItems,
    totalPaid: clientBalance?.totalPaid,
    totalDeposits: clientBalance?.totalDeposits,
    amountDue: clientBalance?.amountDue,
    checkCurrency: clientBalance?.checkCurrency,
    defaultItemCurrency: clientBalance?.defaultItemCurrency,
    showDualCurrency: clientBalance?.showDualCurrency,
    totalInDefaultCurrency: clientBalance?.totalItemsInDefaultCurrency,
    totalPaidInDefaultCurrency: clientBalance?.totalPaidInDefaultCurrency,
    totalDepositsInDefaultCurrency: clientBalance?.totalDepositsInDefaultCurrency,
    amountDueInDefaultCurrency: clientBalance?.amountDueInDefaultCurrency,
  }, t);

  return (
    <>
      <ExchangeRateMissingAlert errorInfo={balanceError} noticeId={`client-balance-fx-${clientId}`} />
      <BalanceSummaryCards
        cards={balanceCards}
        isMobile={isMobile}
        compact
      />

      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>{t('clients.transactionHistory')}</Typography>
      <CheckTransactionsTable
        transactions={clientTransactions}
        paymentCurrency={currency}
        isMobile={isMobile}
        isManager={isManager}
        onCancelPayment={handleCancelDeposit}
        isActionPending={isPending}
        emptyMessage={t('clients.noTransactions')}
        desktopEmptyMode="standalone"
        emptyStandaloneSx={{ py: 3 }}
      />
    </>
  );
}

export default ClientBalanceTab;
